import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import Dropdown from "../../../common/dropDown";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import { formatReceiptStatusLabel } from "../epsfBadges";
import { findOption, renderImpProductCell, useEpsfStudySite } from "../epsfLedgerUi";
import { getStorageTempBadgeTone, toCelsius } from "../epsfTempCompat";
import { formatStorageTempRange } from "./impProductExcel";
import {
  fetchImpBalances,
  fetchImpKits,
  fetchImpTxns,
  fetchStorageUnits,
  fetchTempReadings,
  postTempReading,
  saveStorageUnit,
} from "../epsfService";
import "../EpsfPages.scss";

function formatWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function isExcursionRow(row) {
  return Boolean(row?.IsExcursion || row?.isExcursion);
}

function unitIdOf(row) {
  return String(row?.StorageUnitId || row?.storageUnitId || "");
}

function kitStillOnUnit(kit, unitId) {
  if (String(kit.CurrentStorageUnitId || kit.currentStorageUnitId || "") !== unitId) {
    return false;
  }
  const status = String(kit.Status || kit.status || "");
  return !["Destroyed", "Dispensed", "InTransit"].includes(status);
}

function receiptReceivedCount(row) {
  const kits = Number(row.KitCount ?? row.kitCount ?? 0);
  if (Number.isFinite(kits) && kits > 0) return kits;
  const qty = Number(row.Quantity ?? row.quantity ?? 0);
  return Number.isFinite(qty) ? qty : 0;
}

function receiptUnitLabel(row, count) {
  const kits = Number(row.KitCount ?? row.kitCount ?? 0);
  if (Number.isFinite(kits) && kits > 0) {
    return `${count} kit${count === 1 ? "" : "s"}`;
  }
  const unit = String(row.UnitLabel || row.unitLabel || "").trim();
  return unit ? `${count} ${unit}` : String(count);
}

function enrichStoredReceipts(receipts, kits, balances, unitId) {
  const balanceByBatch = new Map();
  (balances || []).forEach((b) => {
    const key = [
      String(b.ProductCode || b.productCode || "").toLowerCase(),
      String(b.BatchNumber || b.batchNumber || "").toLowerCase(),
    ].join("|");
    balanceByBatch.set(key, b);
  });

  return (receipts || []).map((row) => {
    const txnId = String(row.ImpTxnId || row.impTxnId || "");
    const received = receiptReceivedCount(row);
    const tracking = String(
      row.InventoryTrackingMethod || row.inventoryTrackingMethod || "",
    );
    const isKit =
      tracking === "IndividualKit" ||
      Number(row.KitCount ?? row.kitCount ?? 0) > 0;

    let available = 0;
    if (isKit) {
      available = (kits || []).filter(
        (k) =>
          String(k.ReceiptTxnId || k.receiptTxnId || "") === txnId &&
          kitStillOnUnit(k, unitId),
      ).length;
    } else {
      const key = [
        String(row.ProductCode || row.productCode || "").toLowerCase(),
        String(row.BatchNumber || row.batchNumber || "").toLowerCase(),
      ].join("|");
      const bal = balanceByBatch.get(key);
      const avail = Number(
        bal?.AvailableOnHand ?? bal?.availableOnHand ?? bal?.OnHand ?? bal?.onHand ?? 0,
      );
      available = Number.isFinite(avail) ? Math.max(0, avail) : 0;
    }

    return {
      ...row,
      ReceivedCount: received,
      AvailableCount: available,
      ReceivedLabel: receiptUnitLabel(row, received),
      AvailableLabel: receiptUnitLabel(row, available),
    };
  });
}

function unitAsProductRow(row) {
  return {
    ProductName: row.UnitName || row.unitName || "",
    ProductCode: row.UnitCode || row.unitCode || "",
  };
}

function renderTempRangeBadge(row) {
  const min = row.MinTempC ?? row.minTempC;
  const max = row.MaxTempC ?? row.maxTempC;
  const unit = row.TempUnit || row.tempUnit || "C";
  const label = formatStorageTempRange(min, max, unit, "");
  if (!label) return "—";
  const tone = getStorageTempBadgeTone(min, max, unit);
  return (
    <span
      className={`epsf-imp-pill epsf-imp-pill--temp-${tone}`}
      title={label}
    >
      {label}
    </span>
  );
}

/** Logged reading (°C) vs unit configured range — within / outside / null. */
function readingRangeStatus(tempC, row) {
  const t = Number(tempC);
  if (!Number.isFinite(t)) return null;
  const minC = toCelsius(row?.MinTempC ?? row?.minTempC, row?.TempUnit || row?.tempUnit || "C");
  const maxC = toCelsius(row?.MaxTempC ?? row?.maxTempC, row?.TempUnit || row?.tempUnit || "C");
  if (minC == null || maxC == null) return null;
  if (t < minC || t > maxC) return "outside";
  return "within";
}

function formatReadingTempLabel(tempC) {
  const t = Number(tempC);
  if (!Number.isFinite(t)) return "—";
  const fmt = Number.isInteger(t) ? String(t) : String(Math.round(t * 100) / 100);
  return `${fmt} °C`;
}

function renderLastReadingTempBadge(tempC, row) {
  const label = formatReadingTempLabel(tempC);
  const status = readingRangeStatus(tempC, row);
  const toneClass =
    status === "within"
      ? "epsf-imp-pill--reading-within"
      : status === "outside"
        ? "epsf-imp-pill--reading-outside"
        : "epsf-imp-pill--temp-default";
  const title =
    status === "within"
      ? `${label} — within unit range`
      : status === "outside"
        ? `${label} — outside unit range`
        : label;
  return (
    <span className={`epsf-imp-pill ${toneClass}`} title={title}>
      {label}
    </span>
  );
}

function readingStatusBadge(tempC, row, excursion) {
  if (excursion) {
    return { label: "Excursion", tone: "danger" };
  }
  const status = readingRangeStatus(tempC, row);
  if (status === "within") return { label: "In range", tone: "success" };
  if (status === "outside") return { label: "Out of range", tone: "danger" };
  return { label: "Reading", tone: "info" };
}

const UNIT_FIELD_OPTIONS = [
  { key: "Unit", label: "Storage unit" },
  { key: "RangeC", label: "Temperature" },
  { key: "RackCount", label: "Racks" },
  { key: "LastReading", label: "Last reading" },
  { key: "KitCountOnHand", label: "Items stored" },
  { key: "LocationLabel", label: "Location" },
];

const UNIT_DEFAULT_COLUMNS = [
  "Unit",
  "RangeC",
  "RackCount",
  "LastReading",
  "KitCountOnHand",
  "LocationLabel",
];

const emptyUnitForm = () => ({
  storageUnitId: "",
  unitCode: "",
  unitName: "",
  locationLabel: "",
  minTempC: "2",
  maxTempC: "8",
  tempUnit: "C",
  rackCount: "4",
});

const emptyReadingForm = () => ({
  storageUnitId: "",
  temperatureC: "",
  readingAt: new Date(),
  referenceNo: "",
  notes: "",
  markExcursion: false,
  excursionNotes: "",
});

export default function ImpStorage() {
  const { loading, canView, moduleAccess } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();

  const [units, setUnits] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState("");

  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [busyUnit, setBusyUnit] = useState(false);
  const [busyReading, setBusyReading] = useState(false);
  const [unitFormError, setUnitFormError] = useState("");
  const [readingFormError, setReadingFormError] = useState("");
  const [unitForm, setUnitForm] = useState(emptyUnitForm);
  const [readingForm, setReadingForm] = useState(emptyReadingForm);

  const [detailUnit, setDetailUnit] = useState(null);
  const [detailReadings, setDetailReadings] = useState([]);
  const [detailReceipts, setDetailReceipts] = useState([]);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState("");

  const unitOptions = useMemo(
    () =>
      units.map((u) => {
        const id = unitIdOf(u);
        return {
          value: id,
          label: `${u.UnitCode || u.unitCode} - ${u.UnitName || u.unitName}`,
        };
      }),
    [units],
  );

  const unitOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select unit" }, ...unitOptions],
    [unitOptions],
  );

  const refresh = useCallback(async () => {
    setError("");
    if (!ctx.studyId || !ctx.siteId) {
      setUnits([]);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const unitRes = await fetchStorageUnits(ctx.studyId, ctx.siteId);
      if (unitRes.success) setUnits(unitRes.rows || []);
      else if (unitRes.message) setError(unitRes.message);
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadUnitDetail = useCallback(
    async (unit) => {
      const id = unitIdOf(unit);
      if (!ctx.studyId || !ctx.siteId || !id) {
        setDetailReadings([]);
        setDetailReceipts([]);
        return;
      }
      setDetailBusy(true);
      setDetailError("");
      try {
        const [readingRes, txnRes, kitRes, balRes] = await Promise.all([
          fetchTempReadings(ctx.studyId, ctx.siteId, id),
          fetchImpTxns(ctx.studyId, ctx.siteId, "Receipt"),
          fetchImpKits(ctx.studyId, ctx.siteId),
          fetchImpBalances(ctx.studyId, ctx.siteId),
        ]);
        if (readingRes.success) setDetailReadings(readingRes.rows || []);
        else {
          setDetailReadings([]);
          if (readingRes.message) setDetailError(readingRes.message);
        }
        if (txnRes.success) {
          const receipts = (txnRes.rows || []).filter(
            (r) => unitIdOf(r) === id,
          );
          setDetailReceipts(
            enrichStoredReceipts(
              receipts,
              kitRes.success ? kitRes.rows || [] : [],
              balRes.success ? balRes.rows || [] : [],
              id,
            ),
          );
        } else {
          setDetailReceipts([]);
          if (txnRes.message && !readingRes.message) {
            setDetailError(txnRes.message);
          }
        }
      } finally {
        setDetailBusy(false);
      }
    },
    [ctx.studyId, ctx.siteId],
  );

  const openUnitDetail = useCallback(
    (row) => {
      if (!row) return;
      const id = unitIdOf(row);
      const live = units.find((u) => unitIdOf(u) === id) || row;
      setDetailUnit(live);
      setDetailReadings([]);
      setDetailReceipts([]);
      setDetailError("");
      loadUnitDetail(live);
    },
    [units, loadUnitDetail],
  );

  const closeUnitDetail = () => {
    setDetailUnit(null);
    setDetailReadings([]);
    setDetailReceipts([]);
    setDetailError("");
  };

  const detailUnitId = detailUnit ? unitIdOf(detailUnit) : "";

  // Keep detail header in sync when list refreshes after a new reading.
  useEffect(() => {
    if (!detailUnitId) return;
    const live = units.find((u) => unitIdOf(u) === detailUnitId);
    if (live) setDetailUnit(live);
  }, [units, detailUnitId]);

  const openAddUnit = () => {
    setUnitForm(emptyUnitForm());
    setUnitFormError("");
    setError("");
    setShowUnitModal(true);
  };

  const closeUnitModal = () => {
    if (busyUnit) return;
    setShowUnitModal(false);
    setUnitFormError("");
  };

  const openLogReading = useCallback(
    (storageUnitId = "") => {
      const onlyId =
        storageUnitId ||
        (units.length === 1
          ? String(units[0].StorageUnitId || units[0].storageUnitId || "")
          : "");
      setReadingForm({ ...emptyReadingForm(), storageUnitId: onlyId });
      setReadingFormError("");
      setError("");
      setShowReadingModal(true);
    },
    [units],
  );

  const closeReadingModal = () => {
    if (busyReading) return;
    setShowReadingModal(false);
    setReadingFormError("");
  };

  const onSaveUnit = async (e) => {
    e.preventDefault();
    setUnitFormError("");
    if (!ctx.studyId || !ctx.siteId) {
      setUnitFormError("Select study and site in the breadcrumb bar.");
      return;
    }
    if (!unitForm.unitCode.trim() || !unitForm.unitName.trim()) {
      setUnitFormError("Unit code and name are required.");
      return;
    }
    const minVal = unitForm.minTempC === "" ? null : Number(unitForm.minTempC);
    const maxVal = unitForm.maxTempC === "" ? null : Number(unitForm.maxTempC);
    if (minVal == null || maxVal == null) {
      setUnitFormError("Minimum and maximum temperature are both required.");
      return;
    }
    if (
      !Number.isFinite(minVal) ||
      !Number.isFinite(maxVal) ||
      minVal >= maxVal
    ) {
      setUnitFormError(
        "Minimum temperature must be lower than maximum temperature.",
      );
      return;
    }
    if (!unitForm.tempUnit) {
      setUnitFormError("Temperature unit is required.");
      return;
    }
    const rackCount = Number(unitForm.rackCount);
    if (
      !Number.isFinite(rackCount) ||
      !Number.isInteger(rackCount) ||
      rackCount < 1 ||
      rackCount > 200
    ) {
      setUnitFormError("Enter how many racks this unit has (1–200).");
      return;
    }
    const tempUnit =
      String(unitForm.tempUnit || "C").toUpperCase() === "F" ? "F" : "C";
    setBusyUnit(true);
    try {
      const result = await saveStorageUnit({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        StorageUnitId: unitForm.storageUnitId || null,
        UnitCode: unitForm.unitCode.trim(),
        UnitName: unitForm.unitName.trim(),
        LocationLabel: unitForm.locationLabel.trim(),
        MinTempC: minVal,
        MaxTempC: maxVal,
        TempUnit: tempUnit,
        RackCount: rackCount,
        IsActive: true,
      });
      if (!result.success) {
        setUnitFormError(result.message || "Could not save storage unit.");
        return;
      }
      setShowUnitModal(false);
      setUnitForm(emptyUnitForm());
      await refresh();
    } finally {
      setBusyUnit(false);
    }
  };

  const onPostReading = async (e) => {
    e.preventDefault();
    setReadingFormError("");
    if (!ctx.studyId || !ctx.siteId || !readingForm.storageUnitId) {
      setReadingFormError("Select study, site and storage unit.");
      return;
    }
    const temp = Number(readingForm.temperatureC);
    if (Number.isNaN(temp)) {
      setReadingFormError("Enter a valid temperature.");
      return;
    }
    setBusyReading(true);
    try {
      const result = await postTempReading({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        StorageUnitId: readingForm.storageUnitId,
        TemperatureC: temp,
        ReadingAt: readingForm.readingAt
          ? new Date(readingForm.readingAt).toISOString()
          : null,
        ReferenceNo: readingForm.referenceNo.trim(),
        Notes: readingForm.notes.trim(),
        IsExcursion: readingForm.markExcursion || null,
        ExcursionNotes: readingForm.excursionNotes.trim(),
      });
      if (!result.success) {
        setReadingFormError(result.message || "Could not record temperature.");
        return;
      }
      const postedUnitId = String(readingForm.storageUnitId);
      setShowReadingModal(false);
      setReadingForm(emptyReadingForm());
      await refresh();
      if (detailUnit && unitIdOf(detailUnit) === postedUnitId) {
        await loadUnitDetail({
          ...detailUnit,
          StorageUnitId: postedUnitId,
        });
      }
    } finally {
      setBusyReading(false);
    }
  };

  const setUnitField = (key) => (ev) =>
    setUnitForm((prev) => ({ ...prev, [key]: ev.target.value }));

  const setReadingField = (key) => (ev) =>
    setReadingForm((prev) => ({ ...prev, [key]: ev.target.value }));

  const renderUnitCell = useCallback(
    (row, field) => {
      if (field === "Unit") {
        return renderImpProductCell(unitAsProductRow(row), {
          as: "button",
          type: "button",
          className: "report-table__cell-link epsf-imp-product-cell",
          title: "Open storage unit",
          onClick: () => openUnitDetail(row),
        });
      }
      if (field === "LocationLabel") {
        return row.LocationLabel || row.locationLabel || "—";
      }
      if (field === "RackCount") {
        const n = Number(row.RackCount ?? row.rackCount ?? 0);
        if (!Number.isFinite(n) || n <= 0) return "—";
        return `${n} rack${n === 1 ? "" : "s"}`;
      }
      if (field === "RangeC") {
        return renderTempRangeBadge(row);
      }
      if (field === "LastReading") {
        const unitId = unitIdOf(row);
        const tempC = row.LastTemperatureC ?? row.lastTemperatureC;
        const hasReading = tempC != null && tempC !== "";
        const when = formatWhen(row.LastReadingAt || row.lastReadingAt);
        return (
          <div className="epsf-storage-last-reading">
            {hasReading ? (
              <>
                {renderLastReadingTempBadge(tempC, row)}
                <span
                  className="epsf-storage-last-reading__when"
                  title={when}
                >
                  {when}
                </span>
              </>
            ) : (
              <span className="epsf-storage-last-reading__empty">
                No reading yet
              </span>
            )}
            <button
              type="button"
              className="epsf-storage-last-reading__action"
              title="Log a temperature reading for this unit"
              onClick={() => openLogReading(unitId)}
            >
              Log reading
            </button>
          </div>
        );
      }
      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    [openLogReading, openUnitDetail],
  );

  const emptyStudySite =
    !ctx.studyId || !ctx.siteId
      ? "Select study and site in the breadcrumb bar."
      : null;

  const detailKitCount =
    detailUnit?.KitCountOnHand ?? detailUnit?.kitCountOnHand ?? 0;
  const detailBatchCount =
    detailUnit?.LinkedBatchCount ?? detailUnit?.linkedBatchCount ?? 0;
  const detailReceiptTotalReceived = detailReceipts.reduce(
    (sum, row) => sum + Number(row.ReceivedCount || 0),
    0,
  );
  const detailReceiptTotalAvailable = detailReceipts.reduce(
    (sum, row) => sum + Number(row.AvailableCount || 0),
    0,
  );
  const detailLocation =
    detailUnit?.LocationLabel || detailUnit?.locationLabel || "—";
  const detailRackCount = Number(
    detailUnit?.RackCount ?? detailUnit?.rackCount ?? 0,
  );
  const detailTempC =
    detailUnit?.LastTemperatureC ?? detailUnit?.lastTemperatureC;
  const detailHasReading = detailTempC != null && detailTempC !== "";
  const detailWhen = formatWhen(
    detailUnit?.LastReadingAt || detailUnit?.lastReadingAt,
  );

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Storage & temperature" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: refresh },
            {
              icon: "fas fa-plus",
              label: "Add unit",
              onClick: openAddUnit,
            },
          ]}
        />

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        <div className="epsf-report-wrap">
          <ReportTable
            rows={units}
            loading={listLoading}
            fieldOptions={UNIT_FIELD_OPTIONS}
            defaultColumns={UNIT_DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              UNIT_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderUnitCell}
            getRowKey={(row, index) => unitIdOf(row) || String(index)}
            persistLayoutKey="epsf-imp-storage-units-v6"
            emptyMessage={
              emptyStudySite || "No storage units yet for this study and site."
            }
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
          />
        </div>

        {detailUnit ? (
          <div
            className="modal fade show d-block epsf-modal epsf-storage-detail-modal"
            tabIndex="-1"
          >
            <div className="modal-dialog modal-xl epsf-storage-detail-modal__dialog">
              <div className="modal-content epsf-storage-detail-modal__content">
                <div className="modal-header epsf-storage-detail-modal__header">
                  <h5 className="modal-title mb-0">Storage unit</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeUnitDetail}
                  />
                </div>

                <div className="modal-body epsf-storage-detail-modal__body">
                  {detailError ? (
                    <p className="epsf-list-page__error mb-2">{detailError}</p>
                  ) : null}

                  <div className="epsf-storage-detail-modal__layout">
                    <section className="epsf-storage-detail-modal__col epsf-storage-detail-modal__col--details">
                      <div className="epsf-storage-detail-toolbar">
                        <h6 className="epsf-storage-detail-section-title">
                          Details
                        </h6>
                      </div>
                      <div className="epsf-storage-detail-meta">
                        <div className="epsf-storage-detail-meta__item">
                          <span className="epsf-storage-detail-meta__label">
                            Storage unit
                          </span>
                          <span className="epsf-storage-detail-meta__value">
                            {renderImpProductCell(unitAsProductRow(detailUnit))}
                          </span>
                        </div>
                        <div className="epsf-storage-detail-meta__item">
                          <span className="epsf-storage-detail-meta__label">
                            Location
                          </span>
                          <span className="epsf-storage-detail-meta__value">
                            {detailLocation}
                          </span>
                        </div>
                        <div className="epsf-storage-detail-meta__item">
                          <span className="epsf-storage-detail-meta__label">
                            Racks
                          </span>
                          <span className="epsf-storage-detail-meta__value">
                            {detailRackCount > 0
                              ? `${detailRackCount} rack${
                                  detailRackCount === 1 ? "" : "s"
                                }`
                              : "None configured"}
                          </span>
                        </div>
                        <div className="epsf-storage-detail-meta__item">
                          <span className="epsf-storage-detail-meta__label">
                            Temperature
                          </span>
                          <span className="epsf-storage-detail-meta__value">
                            {renderTempRangeBadge(detailUnit)}
                          </span>
                        </div>
                        <div className="epsf-storage-detail-meta__item">
                          <span className="epsf-storage-detail-meta__label">
                            Items stored
                          </span>
                          <span className="epsf-storage-detail-meta__value">
                            {detailKitCount} kit
                            {Number(detailKitCount) === 1 ? "" : "s"}
                            {Number(detailBatchCount) > 0
                              ? ` · ${detailBatchCount} batch${
                                  Number(detailBatchCount) === 1 ? "" : "es"
                                } linked`
                              : ""}
                          </span>
                        </div>
                        <div className="epsf-storage-detail-meta__item">
                          <span className="epsf-storage-detail-meta__label">
                            Last reading
                          </span>
                          <span className="epsf-storage-detail-meta__value epsf-storage-detail-meta__value--reading">
                            {detailHasReading ? (
                              <>
                                {renderLastReadingTempBadge(
                                  detailTempC,
                                  detailUnit,
                                )}
                                <span className="epsf-storage-last-reading__when">
                                  {detailWhen}
                                </span>
                              </>
                            ) : (
                              "No reading yet"
                            )}
                          </span>
                        </div>
                      </div>
                    </section>

                    <section className="epsf-storage-detail-modal__col">
                      <div className="epsf-storage-detail-toolbar">
                        <h6 className="epsf-storage-detail-section-title">
                          Temperature readings
                        </h6>
                      </div>
                      <div
                        className="epsf-storage-detail-list"
                        role="list"
                        aria-label="Temperature readings"
                      >
                        <div className="epsf-storage-detail-list__actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              openLogReading(unitIdOf(detailUnit))
                            }
                          >
                            <i className="fas fa-plus" aria-hidden="true" />{" "}
                            Log reading
                          </button>
                        </div>
                        {detailBusy && detailReadings.length === 0 ? (
                          <p className="epsf-storage-detail-list__empty">
                            Loading readings…
                          </p>
                        ) : detailReadings.length === 0 ? (
                          <p className="epsf-storage-detail-list__empty">
                            No temperature readings yet for this unit.
                          </p>
                        ) : (
                          detailReadings.map((row, index) => {
                            const tempC =
                              row.TemperatureC ?? row.temperatureC;
                            const when = formatWhen(
                              row.ReadingAt || row.readingAt,
                            );
                            const ref = String(
                              row.ReferenceNo || row.referenceNo || "",
                            ).trim();
                            const notes = String(
                              row.Notes || row.notes || "",
                            ).trim();
                            const excursionNotes = String(
                              row.ExcursionNotes ||
                                row.excursionNotes ||
                                "",
                            ).trim();
                            const recordedBy = String(
                              row.CreatedByName ||
                                row.createdByName ||
                                "",
                            ).trim();
                            const excursion = isExcursionRow(row);
                            const statusBadge = readingStatusBadge(
                              tempC,
                              detailUnit,
                              excursion,
                            );
                            const noteText = [
                              notes,
                              excursionNotes
                                ? `Excursion: ${excursionNotes}`
                                : "",
                            ]
                              .filter(Boolean)
                              .join("\n");
                            const hasDetailBox = Boolean(noteText || ref);
                            return (
                              <article
                                key={String(
                                  row.TempReadingId ||
                                    row.tempReadingId ||
                                    index,
                                )}
                                className="epsf-storage-log-card"
                                role="listitem"
                              >
                                <div className="epsf-storage-log-card__head">
                                  <div className="epsf-storage-log-card__title-wrap">
                                    <span
                                      className="epsf-storage-log-card__icon is-temp"
                                      aria-hidden="true"
                                    >
                                      <i className="fas fa-thermometer-half" />
                                    </span>
                                    <h6 className="epsf-storage-log-card__title">
                                      {formatReadingTempLabel(tempC)}
                                    </h6>
                                  </div>
                                  <span
                                    className={`epsf-storage-log-card__badge is-${statusBadge.tone}`}
                                  >
                                    {statusBadge.label}
                                  </span>
                                </div>
                                {hasDetailBox ? (
                                  <div className="epsf-storage-log-card__notes">
                                    {noteText ? (
                                      <p className="epsf-storage-log-card__notes-line">
                                        <span className="epsf-storage-log-card__notes-key">
                                          Notes:
                                        </span>{" "}
                                        {noteText}
                                      </p>
                                    ) : null}
                                    {ref ? (
                                      <p className="epsf-storage-log-card__notes-line">
                                        <span className="epsf-storage-log-card__notes-key">
                                          Ref:
                                        </span>{" "}
                                        {ref}
                                      </p>
                                    ) : null}
                                  </div>
                                ) : null}
                                <div className="epsf-storage-log-card__meta">
                                  <span className="epsf-storage-log-card__meta-item">
                                    <i
                                      className="far fa-clock"
                                      aria-hidden="true"
                                    />
                                    {when || "—"}
                                  </span>
                                  <span className="epsf-storage-log-card__meta-item">
                                    <i
                                      className="far fa-user"
                                      aria-hidden="true"
                                    />
                                    {recordedBy || "—"}
                                  </span>
                                </div>
                              </article>
                            );
                          })
                        )}
                      </div>
                    </section>

                    <section className="epsf-storage-detail-modal__col">
                      <div className="epsf-storage-detail-toolbar">
                        <h6 className="epsf-storage-detail-section-title">
                          Stored receipts
                        </h6>
                        <span className="epsf-storage-detail-toolbar__count">
                          {detailReceipts.length} receipt
                          {detailReceipts.length === 1 ? "" : "s"}
                          {detailReceipts.length > 0
                            ? ` · received ${detailReceiptTotalReceived} · available ${detailReceiptTotalAvailable}`
                            : ""}
                        </span>
                      </div>
                      <div
                        className="epsf-storage-detail-list"
                        role="list"
                        aria-label="Stored receipts"
                      >
                        {detailBusy && detailReceipts.length === 0 ? (
                          <p className="epsf-storage-detail-list__empty">
                            Loading stored receipts…
                          </p>
                        ) : detailReceipts.length === 0 ? (
                          <p className="epsf-storage-detail-list__empty">
                            No receipts stored against this unit.
                          </p>
                        ) : (
                          detailReceipts.map((row, index) => {
                            const productName = String(
                              row.ProductName || row.productName || "",
                            ).trim();
                            const productCode = String(
                              row.ProductCode || row.productCode || "",
                            ).trim();
                            const title =
                              productName ||
                              productCode ||
                              "Stored receipt";
                            const batch = String(
                              row.BatchNumber || row.batchNumber || "",
                            ).trim();
                            const rackLabel = [
                              row.StorageRackCode || row.storageRackCode,
                              row.StorageRackName || row.storageRackName,
                            ]
                              .filter(Boolean)
                              .join(" — ");
                            const notes = String(
                              row.Notes || row.notes || "",
                            ).trim();
                            const status = String(
                              row.ReceiptStatus || row.receiptStatus || "",
                            ).trim();
                            const statusLabel =
                              formatReceiptStatusLabel(status) || "Receipt";
                            const when = formatWhen(
                              row.TxnAt || row.txnAt || row.CreatedAt,
                            );
                            return (
                              <article
                                key={String(
                                  row.ImpTxnId || row.impTxnId || index,
                                )}
                                className="epsf-storage-log-card"
                                role="listitem"
                              >
                                <div className="epsf-storage-log-card__head">
                                  <div className="epsf-storage-log-card__title-wrap">
                                    <span
                                      className="epsf-storage-log-card__icon is-receipt"
                                      aria-hidden="true"
                                    >
                                      <i className="fas fa-box" />
                                    </span>
                                    <div className="epsf-storage-log-card__title-stack">
                                      <div className="epsf-storage-log-card__title-row">
                                        {productCode &&
                                        productCode !== title ? (
                                          <span className="epsf-storage-log-card__chip">
                                            {productCode}
                                          </span>
                                        ) : null}
                                        <h6 className="epsf-storage-log-card__title">
                                          {title}
                                        </h6>
                                      </div>
                                    </div>
                                  </div>
                                  <span
                                    className={`epsf-storage-log-card__badge is-${
                                      status ? "info" : "neutral"
                                    }`}
                                  >
                                    {statusLabel}
                                  </span>
                                </div>
                                <div className="epsf-storage-log-card__notes">
                                  <div className="epsf-storage-log-card__qty">
                                    <div className="epsf-storage-log-card__qty-col">
                                      <span className="epsf-storage-log-card__qty-label">
                                        Received
                                      </span>
                                      <span className="epsf-storage-log-card__qty-value">
                                        {row.ReceivedLabel || "—"}
                                      </span>
                                    </div>
                                    <div className="epsf-storage-log-card__qty-col">
                                      <span className="epsf-storage-log-card__qty-label">
                                        Available
                                      </span>
                                      <span className="epsf-storage-log-card__qty-value">
                                        {row.AvailableLabel || "—"}
                                      </span>
                                    </div>
                                  </div>
                                  {batch ? (
                                    <p className="epsf-storage-log-card__notes-line">
                                      <span className="epsf-storage-log-card__notes-key">
                                        Batch:
                                      </span>{" "}
                                      {batch}
                                    </p>
                                  ) : null}
                                  {rackLabel ? (
                                    <p className="epsf-storage-log-card__notes-line">
                                      <span className="epsf-storage-log-card__notes-key">
                                        Storage rack:
                                      </span>{" "}
                                      {rackLabel}
                                    </p>
                                  ) : null}
                                  {notes ? (
                                    <p className="epsf-storage-log-card__notes-line">
                                      <span className="epsf-storage-log-card__notes-key">
                                        Notes:
                                      </span>{" "}
                                      {notes}
                                    </p>
                                  ) : null}
                                </div>
                                <div className="epsf-storage-log-card__meta">
                                  <span className="epsf-storage-log-card__meta-item">
                                    <i
                                      className="far fa-clock"
                                      aria-hidden="true"
                                    />
                                    {when || "—"}
                                  </span>
                                  <span className="epsf-storage-log-card__meta-item">
                                    <i
                                      className="far fa-user"
                                      aria-hidden="true"
                                    />
                                    {String(
                                      row.ReceivedByName ||
                                        row.receivedByName ||
                                        row.CreatedByName ||
                                        row.createdByName ||
                                        "",
                                    ).trim() || "—"}
                                  </span>
                                </div>
                              </article>
                            );
                          })
                        )}
                      </div>
                    </section>
                  </div>
                </div>

                <div className="modal-footer epsf-storage-detail-modal__footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeUnitDetail}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {showUnitModal ? (
          <div
            className="modal fade show d-block epsf-modal"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={onSaveUnit}>
                  <div className="modal-header">
                    <h5 className="modal-title">Add storage unit</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeUnitModal}
                      disabled={busyUnit}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Unit code <span className="mandatory">*</span>
                          </label>
                          <input
                            className="form-control"
                            value={unitForm.unitCode}
                            onChange={setUnitField("unitCode")}
                            placeholder="FRIDGE-01"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Unit name <span className="mandatory">*</span>
                          </label>
                          <input
                            className="form-control"
                            value={unitForm.unitName}
                            onChange={setUnitField("unitName")}
                            placeholder="Pharmacy fridge 1"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="epsf-form-field">
                          <label className="form-label">Location</label>
                          <input
                            className="form-control"
                            value={unitForm.locationLabel}
                            onChange={setUnitField("locationLabel")}
                            placeholder="Dispensary / Room 2"
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Min temperature <span className="mandatory">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            step="any"
                            value={unitForm.minTempC}
                            onChange={setUnitField("minTempC")}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Max temperature <span className="mandatory">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            step="any"
                            value={unitForm.maxTempC}
                            onChange={setUnitField("maxTempC")}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Unit <span className="mandatory">*</span>
                          </label>
                          <Dropdown
                            options={[
                              { value: "C", label: "°C" },
                              { value: "F", label: "°F" },
                            ]}
                            selected={findOption(
                              [
                                { value: "C", label: "°C" },
                                { value: "F", label: "°F" },
                              ],
                              unitForm.tempUnit || "C",
                            )}
                            onSelect={(opt) =>
                              setUnitForm((prev) => ({
                                ...prev,
                                tempUnit: opt?.value || "C",
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Number of racks <span className="mandatory">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            min={1}
                            max={200}
                            step={1}
                            value={unitForm.rackCount}
                            onChange={setUnitField("rackCount")}
                            placeholder="e.g. 4"
                            required
                          />
                          <p className="form-text text-muted mb-0">
                            Creates Rack 1 … Rack N for selection on receipt.
                          </p>
                        </div>
                      </div>
                    </div>
                    {unitFormError ? (
                      <p className="epsf-list-page__error mt-2 mb-0">
                        {unitFormError}
                      </p>
                    ) : null}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeUnitModal}
                      disabled={busyUnit}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={busyUnit}
                    >
                      {busyUnit ? "Saving..." : "Save unit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}

        {showReadingModal ? (
          <div
            className="modal fade show d-block epsf-modal"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={onPostReading}>
                  <div className="modal-header">
                    <h5 className="modal-title">Log temperature reading</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={closeReadingModal}
                      disabled={busyReading}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Storage unit <span className="mandatory">*</span>
                          </label>
                          <Dropdown
                            options={unitOptionsWithPlaceholder}
                            selected={findOption(
                              unitOptionsWithPlaceholder,
                              readingForm.storageUnitId,
                            )}
                            onSelect={(opt) =>
                              setReadingForm((prev) => ({
                                ...prev,
                                storageUnitId: opt?.value || "",
                              }))
                            }
                            showSearch
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Temperature °C <span className="mandatory">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="number"
                            step="any"
                            value={readingForm.temperatureC}
                            onChange={setReadingField("temperatureC")}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">
                            Reading time <span className="mandatory">*</span>
                          </label>
                          <DatePicker
                            selected={readingForm.readingAt}
                            onChange={(date) =>
                              setReadingForm((prev) => ({
                                ...prev,
                                readingAt: date,
                              }))
                            }
                            placeholderText="Select date and time"
                            className="form-control"
                            dateFormat="dd/MM/yyyy HH:mm"
                            showTimeSelect
                            timeIntervals={15}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            onChangeRaw={(e) => e.preventDefault()}
                            popperProps={{ strategy: "fixed" }}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="epsf-form-field">
                          <label className="form-label">Reference</label>
                          <input
                            className="form-control"
                            value={readingForm.referenceNo}
                            onChange={setReadingField("referenceNo")}
                            placeholder="Logger / checklist ref"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="epsf-form-field">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={readingForm.notes}
                            onChange={setReadingField("notes")}
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="epsf-form-field">
                          <label className="form-label d-flex align-items-center gap-2 mb-0">
                            <input
                              type="checkbox"
                              checked={readingForm.markExcursion}
                              onChange={(ev) =>
                                setReadingForm((prev) => ({
                                  ...prev,
                                  markExcursion: ev.target.checked,
                                }))
                              }
                            />
                            <span>
                              Mark as excursion (also auto-flagged if outside
                              unit range)
                            </span>
                          </label>
                        </div>
                      </div>
                      {readingForm.markExcursion ? (
                        <div className="col-12">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Excursion notes
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={readingForm.excursionNotes}
                              onChange={setReadingField("excursionNotes")}
                              placeholder="What happened / follow-up"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                    {readingFormError ? (
                      <p className="epsf-list-page__error mt-2 mb-0">
                        {readingFormError}
                      </p>
                    ) : null}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeReadingModal}
                      disabled={busyReading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={busyReading}
                    >
                      {busyReading ? "Saving..." : "Post temperature"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </EpsfAccessGate>
  );
}
